import React, { useState, useEffect } from "react";
import { FaStar, FaRegStar, FaPlus, FaCheck, FaClock, FaTimes, FaEyeSlash } from "react-icons/fa";
import TestimonialManager from "../../../services/dashboard/TestimonialManager";
import { useSelector } from "react-redux";

const UserTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  
  const user = useSelector((state) => state.auth.user);
  
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    persona: "",
    text: "",
    rating: 5
  });

  useEffect(() => {
    if (user?.id) {
      fetchUserTestimonials();
      // Pre-fill form with user data
      setFormData(prev => ({
        ...prev,
        name: `${user.firstName} ${user.lastName}`.trim()
      }));
    }
  }, [user]);

  const fetchUserTestimonials = async () => {
    try {
      const data = await TestimonialManager.getUserTestimonials(user.id);
      setTestimonials(data || []);
    } catch (error) {
      console.error("Failed to fetch user testimonials:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const testimonialData = {
        ...formData,
        userId: user.id,
        avatar: formData.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
      };
      
      await TestimonialManager.submitTestimonial(testimonialData);
      setMessage("✅ Thank you! Your testimonial has been submitted for review.");
      setShowForm(false);
      setFormData({
        name: `${user.firstName} ${user.lastName}`.trim(),
        location: "",
        persona: "",
        text: "",
        rating: 5
      });
      fetchUserTestimonials();
      
      setTimeout(() => setMessage(""), 5000);
    } catch (error) {
      setMessage("❌ Failed to submit testimonial. Please try again.");
      setTimeout(() => setMessage(""), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const formatDateTimeFromArray = (arr) => {
    if (!Array.isArray(arr) || arr.length < 6) return "";
    
    const dateTime = new Date(
      arr[0],        // year
      arr[1] - 1,    // month (0-indexed)
      arr[2],        // day
      arr[3] || 0,   // hour
      arr[4] || 0,   // minute
      arr[5] || 0    // second
    ).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
    
    return dateTime;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: {
        icon: FaClock,
        bgColor: "bg-yellow-50",
        textColor: "text-yellow-700",
        borderColor: "border-yellow-200",
        label: "Under Review"
      },
      APPROVED: {
        icon: FaCheck,
        bgColor: "bg-green-50",
        textColor: "text-green-700",
        borderColor: "border-green-200",
        label: "Published"
      },
      REJECTED: {
        icon: FaTimes,
        bgColor: "bg-red-50",
        textColor: "text-red-700",
        borderColor: "border-red-200",
        label: "Not Approved"
      },
      ARCHIVED: {
        icon: FaEyeSlash,
        bgColor: "bg-gray-50",
        textColor: "text-gray-700",
        borderColor: "border-gray-200",
        label: "Archived"
      }
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          star <= rating ? (
            <FaStar key={star} className="w-4 h-4 text-yellow-400" />
          ) : (
            <FaRegStar key={star} className="w-4 h-4 text-gray-300" />
          )
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">My Testimonials</h2>
          <p className="text-sm text-gray-600 mt-1">Share your experience with Books@Doorstep</p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <FaPlus className="w-4 h-4" />
            Write Testimonial
          </button>
        )}
      </div>

      {/* Message */}
      {message && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 text-sm font-medium">{message}</p>
        </div>
      )}

      {/* Testimonial Form */}
      {showForm && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Write Your Testimonial</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Your city"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reading Persona
              </label>
              <input
                type="text"
                name="persona"
                value={formData.persona}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="e.g., Avid Reader, Weekend Reader, Fiction Lover"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Experience *
              </label>
              <textarea
                name="text"
                value={formData.text}
                onChange={handleInputChange}
                required
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                placeholder="Share your experience with Books@Doorstep..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rating *
              </label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => handleRatingClick(star)}
                    className="p-1 hover:scale-110 transition-transform"
                  >
                    {star <= formData.rating ? (
                      <FaStar className="w-6 h-6 text-yellow-400" />
                    ) : (
                      <FaRegStar className="w-6 h-6 text-gray-300" />
                    )}
                  </button>
                ))}
                <span className="ml-2 text-sm text-gray-600">
                  {formData.rating} star{formData.rating !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Submitting..." : "Submit Testimonial"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* User's Testimonials */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading your testimonials...</p>
        </div>
      ) : testimonials.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">You haven't written any testimonials yet.</p>
          <p className="text-sm text-gray-400 mt-1">Share your experience to help other readers!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {testimonials.map((testimonial) => (
            <div key={testimonial.testimonialId} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg flex items-center justify-center text-white text-sm font-bold">
                    {testimonial.avatar || testimonial.name?.charAt(0) || "?"}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{testimonial.name}</h4>
                    {testimonial.location && (
                      <p className="text-sm text-gray-500">{testimonial.location}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {renderStars(testimonial.rating)}
                  {getStatusBadge(testimonial.status)}
                </div>
              </div>
              
              {testimonial.persona && (
                <p className="text-sm text-orange-600 font-medium mb-2">{testimonial.persona}</p>
              )}
              
              <p className="text-gray-700 leading-relaxed mb-3">{testimonial.text}</p>
              
              <div className="text-xs text-gray-500">
                Submitted on {testimonial.creationDate ? (
                  Array.isArray(testimonial.creationDate) 
                    ? formatDateTimeFromArray(testimonial.creationDate)
                    : new Date(testimonial.creationDate).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })
                ) : 'Unknown date'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserTestimonials;